import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, LockIcon, UnlockIcon, CalendarIcon, ImageIcon, ExternalLinkIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, FilterIcon, XIcon } from 'lucide-react';
import { WhatsOnEvent } from '../../../data/whatsOn';
interface InvitationsManagementProps {
  onBack: () => void;
}
export function InvitationsManagement({
  onBack
}: InvitationsManagementProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInvitation, setEditingInvitation] = useState<WhatsOnEvent | null>(null);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 50;
  // Quick filter states
  const [showInviteOnly, setShowInviteOnly] = useState(false);
  const [showPublic, setShowPublic] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  // Mock data - in production this would come from API
  const [invitations, setInvitations] = useState<WhatsOnEvent[]>([{
    id: 'padel-tournament',
    title: 'Padel Tournament',
    date: 'Thursday, 23 October',
    description: 'Limited slots available. Kickstart the weekend at Rally Padel Club with industry friends.',
    imageUrl: 'https://a.storyblok.com/f/282702/1000x1000/4feff03437/padel.jpg',
    inviteOnly: true,
    rsvpRequired: true,
    eventId: 'industry'
  }, {
    id: 'official-pre-opening',
    title: 'Official Pre-Opening',
    date: 'Thursday, 23 October',
    description: 'Invite-only event welcoming key stakeholders.',
    imageUrl: 'https://a.storyblok.com/f/282702/1000x1000/617c27bce5/roxy.jpg',
    inviteOnly: true,
    rsvpRequired: true,
    eventId: 'industry'
  }, {
    id: 'dubai-muscle-talks',
    title: 'Dubai Muscle Talks',
    date: '24 - 26 October',
    description: 'Join engaging Q&A sessions with fitness legends.',
    imageUrl: 'https://a.storyblok.com/f/282702/1000x1000/3a08e088a1/dubai-muscle-talks.jpg',
    inviteOnly: false,
    rsvpRequired: false,
    eventId: 'muscle'
  }, {
    id: 'active-arena',
    title: 'Active Arena',
    date: '24 - 26 October',
    description: 'Experience electrifying workouts led by headline talent.',
    imageUrl: 'https://a.storyblok.com/f/282702/1000x727/27676393f9/active-arena.jpg',
    inviteOnly: false,
    rsvpRequired: false,
    eventId: 'active'
  }]);
  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [formEventId, setFormEventId] = useState<'industry' | 'active' | 'muscle'>('industry');
  const [formInviteOnly, setFormInviteOnly] = useState(false);
  const [formRsvpRequired, setFormRsvpRequired] = useState(false);
  const [formCtaLabel, setFormCtaLabel] = useState('');
  const [formCtaUrl, setFormCtaUrl] = useState('');
  // Close filters dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setShowFiltersDropdown(false);
      }
    };
    if (showFiltersDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFiltersDropdown]);
  const clearFilters = () => {
    setSelectedEvent('all');
    setShowInviteOnly(false);
    setShowPublic(false);
  };
  const activeFiltersCount = (selectedEvent !== 'all' ? 1 : 0) + (showInviteOnly ? 1 : 0) + (showPublic ? 1 : 0);
  const filteredInvitations = invitations.filter(inv => {
    const matchesSearch = searchQuery === '' || inv.title.toLowerCase().includes(searchQuery.toLowerCase()) || inv.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEvent = selectedEvent === 'all' || inv.eventId === selectedEvent;
    // Access filter - supports multiple selections
    let matchesAccess = true;
    if (showInviteOnly || showPublic) {
      matchesAccess = showInviteOnly && inv.inviteOnly || showPublic && !inv.inviteOnly;
    }
    return matchesSearch && matchesEvent && matchesAccess;
  });
  const totalPages = Math.ceil(filteredInvitations.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentInvitations = filteredInvitations.slice(startIndex, endIndex);
  const getEventBadgeVariant = (eventId: string) => {
    switch (eventId) {
      case 'industry':
        return 'default';
      case 'muscle':
        return 'error';
      case 'active':
        return 'success';
      default:
        return 'default';
    }
  };
  const getEventLabel = (eventId: string) => {
    switch (eventId) {
      case 'industry':
        return 'Dubai Active Industry';
      case 'muscle':
        return 'Dubai Muscle Show';
      case 'active':
        return 'Dubai Active Show';
      default:
        return eventId;
    }
  };
  const handleOpenCreateModal = () => {
    setEditingInvitation(null);
    setFormTitle('');
    setFormDate('');
    setFormDescription('');
    setFormImageUrl('');
    setFormEventId('industry');
    setFormInviteOnly(false);
    setFormRsvpRequired(false);
    setFormCtaLabel('');
    setFormCtaUrl('');
    setShowCreateModal(true);
  };
  const handleOpenEditModal = (invitation: WhatsOnEvent) => {
    setEditingInvitation(invitation);
    setFormTitle(invitation.title);
    setFormDate(invitation.date);
    setFormDescription(invitation.description);
    setFormImageUrl(invitation.imageUrl || '');
    setFormEventId(invitation.eventId);
    setFormInviteOnly(invitation.inviteOnly);
    setFormRsvpRequired(invitation.rsvpRequired);
    setFormCtaLabel(invitation.ctaLabel || '');
    setFormCtaUrl(invitation.ctaUrl || '');
    setShowCreateModal(true);
    setShowActionsMenu(null);
  };
  const handleSaveInvitation = () => {
    const invitationData: WhatsOnEvent = {
      id: editingInvitation?.id || `invitation-${Date.now()}`,
      title: formTitle,
      date: formDate,
      description: formDescription,
      imageUrl: formImageUrl || undefined,
      eventId: formEventId,
      inviteOnly: formInviteOnly,
      rsvpRequired: formRsvpRequired,
      ctaLabel: formCtaLabel || undefined,
      ctaUrl: formCtaUrl || undefined
    };
    if (editingInvitation) {
      setInvitations(invitations.map(inv => inv.id === editingInvitation.id ? invitationData : inv));
    } else {
      setInvitations([...invitations, invitationData]);
    }
    setShowCreateModal(false);
  };
  const handleDeleteInvitation = (id: string) => {
    if (confirm('Are you sure you want to delete this invitation?')) {
      setInvitations(invitations.filter(inv => inv.id !== id));
    }
    setShowActionsMenu(null);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  return <div className="space-y-6" data-id="element-1641">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1642">
        <div data-id="element-1643">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-1644">
            Invitations & Events
          </h2>
          <p className="text-gray-500 mt-1" data-id="element-1645">
            Manage What's On events and special invitations
          </p>
        </div>
        <Button variant="primary" onClick={handleOpenCreateModal} data-id="element-1646">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-1647" />
          Create Invitation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1648">
        <Card padding="md" data-id="element-1649">
          <div className="flex items-center justify-between" data-id="element-1650">
            <div data-id="element-1651">
              <p className="text-sm text-gray-500" data-id="element-1652">Total Events</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1653">
                {invitations.length}
              </p>
            </div>
            <CalendarIcon className="w-8 h-8 text-blue-500" data-id="element-1654" />
          </div>
        </Card>
        <Card padding="md" data-id="element-1655">
          <div className="flex items-center justify-between" data-id="element-1656">
            <div data-id="element-1657">
              <p className="text-sm text-gray-500" data-id="element-1658">Invite Only</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1659">
                {invitations.filter(i => i.inviteOnly).length}
              </p>
            </div>
            <LockIcon className="w-8 h-8 text-purple-500" data-id="element-1660" />
          </div>
        </Card>
        <Card padding="md" data-id="element-1661">
          <div className="flex items-center justify-between" data-id="element-1662">
            <div data-id="element-1663">
              <p className="text-sm text-gray-500" data-id="element-1664">RSVP Required</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1665">
                {invitations.filter(i => i.rsvpRequired).length}
              </p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-500" data-id="element-1666" />
          </div>
        </Card>
        <Card padding="md" data-id="element-1667">
          <div className="flex items-center justify-between" data-id="element-1668">
            <div data-id="element-1669">
              <p className="text-sm text-gray-500" data-id="element-1670">Public Events</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1671">
                {invitations.filter(i => !i.inviteOnly).length}
              </p>
            </div>
            <UnlockIcon className="w-8 h-8 text-orange-500" data-id="element-1672" />
          </div>
        </Card>
      </div>

      {/* Enhanced Filters Bar */}
      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-1673">
        <div className="flex gap-4 items-start" data-id="element-1674">
          {/* Search */}
          <div className="relative flex-1 max-w-md" data-id="element-1675">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1676" />
            <input type="text" placeholder="Search invitations..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-inter text-sm" value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1677" />
          </div>

          {/* Quick Filters */}
          <div className="flex gap-2 flex-wrap" data-id="element-1678">
            <button onClick={() => setShowInviteOnly(!showInviteOnly)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showInviteOnly ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1679">
              <LockIcon className="w-4 h-4 inline mr-1.5" data-id="element-1680" />
              Invite Only
            </button>

            <button onClick={() => setShowPublic(!showPublic)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showPublic ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1681">
              <UnlockIcon className="w-4 h-4 inline mr-1.5" data-id="element-1682" />
              Public
            </button>
          </div>

          {/* Advanced Filters Dropdown */}
          <div className="relative" ref={filtersRef} data-id="element-1683">
            <Button variant="outline" size="sm" onClick={() => setShowFiltersDropdown(!showFiltersDropdown)} data-id="element-1684">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-1685" />
              More Filters
              {activeFiltersCount > 0 && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" data-id="element-1686">
                  {activeFiltersCount}
                </span>}
            </Button>

            {showFiltersDropdown && <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-y-auto" data-id="element-1687">
                <div className="p-4" data-id="element-1688">
                  <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b border-gray-100" data-id="element-1689">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-1690">
                      Advanced Filters
                    </h3>
                    {activeFiltersCount > 0 && <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-700" data-id="element-1691">
                        Clear all
                      </button>}
                  </div>

                  {/* Event Filter */}
                  <div data-id="element-1692">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-1693">
                      Event
                    </label>
                    <div className="space-y-2" data-id="element-1694">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1695">
                        <input type="radio" checked={selectedEvent === 'all'} onChange={() => setSelectedEvent('all')} className="text-blue-600 focus:ring-blue-500" data-id="element-1696" />
                        <span className="text-sm text-gray-700" data-id="element-1697">
                          All Events
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1698">
                        <input type="radio" checked={selectedEvent === 'industry'} onChange={() => setSelectedEvent('industry')} className="text-blue-600 focus:ring-blue-500" data-id="element-1699" />
                        <span className="text-sm text-gray-700" data-id="element-1700">
                          Dubai Active Industry
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1701">
                        <input type="radio" checked={selectedEvent === 'muscle'} onChange={() => setSelectedEvent('muscle')} className="text-blue-600 focus:ring-blue-500" data-id="element-1702" />
                        <span className="text-sm text-gray-700" data-id="element-1703">
                          Dubai Muscle Show
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1704">
                        <input type="radio" checked={selectedEvent === 'active'} onChange={() => setSelectedEvent('active')} className="text-blue-600 focus:ring-blue-500" data-id="element-1705" />
                        <span className="text-sm text-gray-700" data-id="element-1706">
                          Dubai Active Show
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 flex-wrap" data-id="element-1707">
            <span className="text-xs font-medium text-gray-500" data-id="element-1708">
              Active filters:
            </span>
            {showInviteOnly && <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1" data-id="element-1709">
                Invite Only
                <button onClick={() => setShowInviteOnly(false)} className="hover:text-purple-900" data-id="element-1710">
                  <XIcon className="w-3 h-3" data-id="element-1711" />
                </button>
              </span>}
            {showPublic && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded flex items-center gap-1" data-id="element-1712">
                Public
                <button onClick={() => setShowPublic(false)} className="hover:text-green-900" data-id="element-1713">
                  <XIcon className="w-3 h-3" data-id="element-1714" />
                </button>
              </span>}
            {selectedEvent !== 'all' && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1" data-id="element-1715">
                {getEventLabel(selectedEvent)}
                <button onClick={() => setSelectedEvent('all')} className="hover:text-gray-900" data-id="element-1716">
                  <XIcon className="w-3 h-3" data-id="element-1717" />
                </button>
              </span>}
          </div>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-id="element-1718">
        <div className="overflow-x-auto" data-id="element-1719">
          <table className="w-full text-left text-sm font-inter" data-id="element-1720">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1721">
              <tr data-id="element-1722">
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[300px]" data-id="element-1723">
                  Event
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[150px]" data-id="element-1724">
                  Date
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[180px]" data-id="element-1725">
                  Assigned Event
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[120px]" data-id="element-1726">
                  Access
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[120px]" data-id="element-1727">
                  RSVP
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 text-right min-w-[80px]" data-id="element-1728">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-1729">
              {currentInvitations.map(invitation => <tr key={invitation.id} className="group hover:bg-gray-50 transition-colors" data-id="element-1730">
                  <td className="px-6 py-4" data-id="element-1731">
                    <div className="flex items-center gap-3" data-id="element-1732">
                      {invitation.imageUrl ? <img src={invitation.imageUrl} alt={invitation.title} className="w-12 h-12 rounded object-cover border border-gray-200" data-id="element-1733" /> : <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center" data-id="element-1734">
                          <ImageIcon className="w-6 h-6 text-gray-300" data-id="element-1735" />
                        </div>}
                      <div data-id="element-1736">
                        <div className="font-medium text-gray-900" data-id="element-1737">
                          {invitation.title}
                        </div>
                        <div className="text-xs text-gray-500 line-clamp-1" data-id="element-1738">
                          {invitation.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600" data-id="element-1739">
                    <div className="flex items-center gap-2" data-id="element-1740">
                      <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-1741" />
                      {invitation.date}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1742">
                    <Badge variant={getEventBadgeVariant(invitation.eventId)} size="sm" data-id="element-1743">
                      {getEventLabel(invitation.eventId)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4" data-id="element-1744">
                    {invitation.inviteOnly ? <Badge variant="default" size="sm" data-id="element-1745">
                        <LockIcon className="w-3 h-3 mr-1" data-id="element-1746" />
                        Invite Only
                      </Badge> : <Badge variant="success" size="sm" data-id="element-1747">
                        <UnlockIcon className="w-3 h-3 mr-1" data-id="element-1748" />
                        Public
                      </Badge>}
                  </td>
                  <td className="px-6 py-4" data-id="element-1749">
                    {invitation.rsvpRequired ? <Badge variant="warning" size="sm" data-id="element-1750">
                        Required
                      </Badge> : <span className="text-sm text-gray-500" data-id="element-1751">Optional</span>}
                  </td>
                  <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()} data-id="element-1752">
                    <div className="relative" data-id="element-1753">
                      <button onClick={() => setShowActionsMenu(showActionsMenu === invitation.id ? null : invitation.id)} className="text-gray-400 hover:text-gray-600" data-id="element-1754">
                        <MoreHorizontalIcon className="w-5 h-5" data-id="element-1755" />
                      </button>
                      {showActionsMenu === invitation.id && <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowActionsMenu(null)} data-id="element-1756" />
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20" data-id="element-1757">
                            <button onClick={() => handleOpenEditModal(invitation)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100" data-id="element-1758">
                              <EditIcon className="w-4 h-4" data-id="element-1759" />
                              Edit
                            </button>
                            <button onClick={() => handleDeleteInvitation(invitation.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-1760">
                              <TrashIcon className="w-4 h-4" data-id="element-1761" />
                              Delete
                            </button>
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex justify-between items-center" data-id="element-1762">
          <span data-id="element-1763">
            Showing {startIndex + 1}-
            {Math.min(endIndex, filteredInvitations.length)} of{' '}
            {filteredInvitations.length} invitations
          </span>
          <div className="flex gap-2 items-center" data-id="element-1764">
            <span className="text-gray-600" data-id="element-1765">
              Page {currentPage} of {totalPages}
            </span>
            <button className="px-3 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled={currentPage === 1} onClick={handlePreviousPage} data-id="element-1766">
              Previous
            </button>
            <button className="px-3 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled={currentPage === totalPages} onClick={handleNextPage} data-id="element-1767">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredInvitations.length === 0 && <Card padding="lg" data-id="element-1768">
          <div className="text-center py-12" data-id="element-1769">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" data-id="element-1770" />
            <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1771">
              No invitations found
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-1772">
              {searchQuery || selectedEvent !== 'all' ? 'Try adjusting your filters' : 'Create your first invitation to get started'}
            </p>
            <Button variant="primary" onClick={handleOpenCreateModal} data-id="element-1773">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-1774" />
              Create Invitation
            </Button>
          </div>
        </Card>}

      {/* Create/Edit Modal */}
      {showCreateModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1775">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-1776">
            <div className="p-6" data-id="element-1777">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1778">
                {editingInvitation ? 'Edit Invitation' : 'Create Invitation'}
              </h3>

              <div className="space-y-4" data-id="element-1779">
                {/* Title */}
                <div data-id="element-1780">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1781">
                    Title *
                  </label>
                  <Input type="text" value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="e.g., Padel Tournament" data-id="element-1782" />
                </div>

                {/* Date */}
                <div data-id="element-1783">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1784">
                    Date *
                  </label>
                  <Input type="text" value={formDate} onChange={e => setFormDate(e.target.value)} placeholder="e.g., Thursday, 23 October" data-id="element-1785" />
                </div>

                {/* Description */}
                <div data-id="element-1786">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1787">
                    Description *
                  </label>
                  <textarea value={formDescription} onChange={e => setFormDescription(e.target.value)} rows={4} placeholder="Describe the event..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-1788" />
                </div>

                {/* Image URL */}
                <div data-id="element-1789">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1790">
                    Image URL
                  </label>
                  <Input type="url" value={formImageUrl} onChange={e => setFormImageUrl(e.target.value)} placeholder="https://example.com/image.jpg" data-id="element-1791" />
                </div>

                {/* Event Selection */}
                <div data-id="element-1792">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1793">
                    Event *
                  </label>
                  <select value={formEventId} onChange={e => setFormEventId(e.target.value as 'industry' | 'active' | 'muscle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1794">
                    <option value="industry" data-id="element-1795">Dubai Active Industry</option>
                    <option value="muscle" data-id="element-1796">Dubai Muscle Show</option>
                    <option value="active" data-id="element-1797">Dubai Active Show</option>
                  </select>
                </div>

                {/* Checkboxes */}
                <div className="space-y-3" data-id="element-1798">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1799">
                    <input type="checkbox" checked={formInviteOnly} onChange={e => setFormInviteOnly(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1800" />
                    <span className="text-sm text-gray-700" data-id="element-1801">
                      Invite Only Event
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1802">
                    <input type="checkbox" checked={formRsvpRequired} onChange={e => setFormRsvpRequired(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1803" />
                    <span className="text-sm text-gray-700" data-id="element-1804">RSVP Required</span>
                  </label>
                </div>

                {/* CTA Fields */}
                <div className="border-t border-gray-200 pt-4" data-id="element-1805">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-1806">
                    Call to Action (Optional)
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1807">
                    <div data-id="element-1808">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1809">
                        CTA Label
                      </label>
                      <Input type="text" value={formCtaLabel} onChange={e => setFormCtaLabel(e.target.value)} placeholder="e.g., Book Tickets" data-id="element-1810" />
                    </div>
                    <div data-id="element-1811">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1812">
                        CTA URL
                      </label>
                      <Input type="url" value={formCtaUrl} onChange={e => setFormCtaUrl(e.target.value)} placeholder="https://..." data-id="element-1813" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6" data-id="element-1814">
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1" data-id="element-1815">
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveInvitation} disabled={!formTitle || !formDate || !formDescription} className="flex-1" data-id="element-1816">
                  {editingInvitation ? 'Save Changes' : 'Create Invitation'}
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}